/*
 * Site-wide quantity-input enforcement.
 *
 * Respecteert min/step/max uit attributes (gezet door Liquid op basis van
 * product metafields custom.minimum_amount / custom.minimum_increase /
 * custom.maximum_amount):
 *
 *  - Clampt handmatige invoer binnen [min, max].
 *  - Snapt naar dichtstbijzijnde step-waarde (≥ min) als user iets tikt
 *    dat niet op een step-boundary valt.
 *  - Synchroniseert disabled-state van -/+ knoppen.
 *  - Werkt voor alle <quantity-input> elementen in het thema (product
 *    card, PDP, quickview, sticky, cart drawer, cart page).
 *  - Sanity-check op page load: als een cart-line qty < min heeft
 *    (bv. via URL-bypass toegevoegd) → auto-bump naar min via theme's
 *    change-event handler.
 *  - Fetch-hook op /cart/add: zachte min-clamping van quantity in body
 *    voor het naar de server gaat (defensief; UI clampt al).
 *  - Cart-aware MAX check op CLICK (capture phase, vóór theme handlers):
 *    leest data-pc-qty-max (metafield) + data-pc-product-id, vergelijkt
 *    met cached cart, blokkeert click als cumulatief totaal het max
 *    zou overschrijden — incl. gesommeerd over alle varianten van
 *    hetzelfde product (optie B: max per PRODUCT). Dispatcht dan een
 *    'cc:cart-max-reached' event dat cart-popup-preference.js opvangt
 *    voor de juiste toast. Geen fetch, geen theme-error popup, geen
 *    drawer-open.
 *
 * Effective max wordt bepaald als min van:
 *   - data-pc-qty-max (metafield maximum_amount)
 *   - max="" attribute (inventory cap, gezet door templates)
 * Als geen van beide gezet → onbegrensd (alleen step en min gelden).
 */
(function () {
  const CART_ADD_RE = /\/cart\/add(\.js)?(\?|$)/;
  const CART_CHANGE_RE = /\/cart\/(change|update|clear)(\.js)?(\?|$)/;

  // ----- Cart cache (voor sync click-check) -----
  let cartCache = null;
  let cartCacheReady = false;
  let cartRefreshTimer = null;

  function refreshCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        cartCache = c;
        cartCacheReady = true;
        // Re-clamp alle qty-inputs zodat selectors en plus-knoppen
        // gelijk de nieuwe "remaining" (= max - inCart) reflecteren.
        // Bv. na een succesvolle add: plus disabled + value gecapt.
        document.querySelectorAll('quantity-input input[type="number"]').forEach(clampInput);
      })
      .catch(function () { /* keep stale cache */ });
  }

  function scheduleCartRefresh() {
    // Coalesce burst-updates (bv. snel meerdere /cart/add achter elkaar)
    clearTimeout(cartRefreshTimer);
    cartRefreshTimer = setTimeout(refreshCart, 100);
  }

  function getProductQtyInCart(productId) {
    if (!cartCacheReady || !cartCache || !cartCache.items) return 0;
    const pid = parseInt(productId, 10);
    if (!isFinite(pid)) return 0;
    let sum = 0;
    for (let i = 0; i < cartCache.items.length; i++) {
      const it = cartCache.items[i];
      if (it && it.product_id === pid) sum += it.quantity;
    }
    return sum;
  }

  function getLimits(input) {
    if (!input) return { min: 1, step: 1, max: null, metaMax: null, invMax: null, productId: null };
    const minAttr = input.dataset.pcQtyMin || input.getAttribute('min');
    const stepAttr = input.dataset.pcQtyStep || input.getAttribute('step');
    const invMaxAttr = input.getAttribute('max');
    const metaMaxAttr = input.dataset.pcQtyMax;
    const min = parseInt(minAttr, 10) || 1;
    const step = parseInt(stepAttr, 10) || 1;
    const invMax = invMaxAttr && isFinite(parseInt(invMaxAttr, 10)) ? parseInt(invMaxAttr, 10) : null;
    const metaMax = metaMaxAttr && isFinite(parseInt(metaMaxAttr, 10)) ? parseInt(metaMaxAttr, 10) : null;
    let max = null;
    if (invMax !== null && metaMax !== null) max = Math.min(invMax, metaMax);
    else if (invMax !== null) max = invMax;
    else if (metaMax !== null) max = metaMax;
    const productId = input.dataset.pcProductId || null;
    return {
      min: min < 1 ? 1 : min,
      step: step < 1 ? 1 : step,
      max,
      metaMax,
      invMax,
      productId,
    };
  }

  function snapToStep(value, min, step) {
    if (value < min) return min;
    if (step <= 1) return value;
    const offset = value - min;
    const stepsUp = Math.ceil(offset / step);
    return min + stepsUp * step;
  }

  // Cart-aware effective max voor add-to-cart inputs (name="quantity"):
  // remaining = limits.max - aantal_in_cart_voor_dit_product. Hierdoor
  // stelt de selector zich automatisch in op wat nog toegevoegd KAN worden.
  // Voor cart-line inputs (name="updates[]") en line items zonder
  // product-id → gebruik gewoon limits.max (clampInput valt terug op normaal).
  function getEffectiveMax(input, limits) {
    if (!limits || limits.max === null) return null;
    if (!cartCacheReady || !limits.productId) return limits.max;
    const isCartLine = input && input.getAttribute && input.getAttribute('name') === 'updates[]';
    if (isCartLine) return limits.max;
    const inCart = getProductQtyInCart(limits.productId);
    return Math.max(0, limits.max - inCart);
  }

  function clampInput(input) {
    if (!input) return;
    const limits = getLimits(input);
    const { min, step } = limits;
    const effMax = getEffectiveMax(input, limits);
    let n = parseInt(input.value, 10);
    if (!isFinite(n) || n < min) n = min;
    n = snapToStep(n, min, step);
    if (effMax !== null) {
      // Cap op effMax, met min als visuele ondergrens (input mag niet
      // onder min komen — als effMax==0 blijft value op min, plus is
      // dan disabled en click wordt geblokkeerd door capture-listener).
      const cap = Math.max(effMax, min);
      if (n > cap) {
        const offset = cap - min;
        const stepsDown = Math.floor(offset / step);
        n = min + stepsDown * step;
        if (n < min) n = min;
      }
    }
    if (String(n) !== input.value) input.value = n;

    const wrap = input.closest('quantity-input');
    if (wrap) {
      const minBtn = wrap.querySelector('button[name="minus"]');
      const plusBtn = wrap.querySelector('button[name="plus"]');
      if (minBtn) minBtn.disabled = n <= min;
      if (plusBtn && effMax !== null) {
        // Disabled als geen ruimte (effMax<=0) OF als value het effMax raakt
        plusBtn.disabled = effMax <= 0 || n >= effMax;
      }
    }
  }

  function initAll() {
    document.querySelectorAll('quantity-input input[type="number"]').forEach(clampInput);
  }

  // ----- Input / +- button events -----
  document.addEventListener('input', function (e) {
    const input = e.target.closest('quantity-input input[type="number"]');
    if (input) clampInput(input);
  });
  document.addEventListener('change', function (e) {
    const input = e.target.closest('quantity-input input[type="number"]');
    if (input) clampInput(input);
  });
  document.addEventListener(
    'blur',
    function (e) {
      const input = e.target.closest('quantity-input input[type="number"]');
      if (input) clampInput(input);
    },
    true
  );
  // Na theme's stepUp/stepDown: resync disabled state
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('quantity-input button[name="plus"], quantity-input button[name="minus"]');
    if (!btn) return;
    const wrap = btn.closest('quantity-input');
    const input = wrap && wrap.querySelector('input[type="number"]');
    if (input) setTimeout(function () { clampInput(input); }, 0);
  });

  // ----- Cart sanity-check -----
  // Als een cart-line qty < min heeft, auto-bump naar min via change-event.
  function sanityCheckCart() {
    document.querySelectorAll('input[name="updates[]"]').forEach(function (input) {
      const min = parseInt(input.dataset.pcQtyMin, 10);
      if (!isFinite(min) || min <= 1) return;
      const current = parseInt(input.value, 10);
      if (!isFinite(current) || current >= min) return;
      const { max } = getLimits(input);
      if (max !== null && min > max) return;
      input.value = min;
      clampInput(input);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  // ----- Click-phase MAX block -----
  // Capture phase listener: vóór de theme z'n add-to-cart click handler
  // afgaat checken we sync tegen de cart-cache. Bij overschrijding
  // stopImmediatePropagation + preventDefault → theme's click handler
  // ziet niets, fetch wordt nooit gedaan, geen cart drawer opent, geen
  // theme error popup. We dispatchen wel een cc:cart-max-reached event
  // zodat cart-popup-preference de juiste toast toont.
  function dispatchMaxReached(detail) {
    document.dispatchEvent(new CustomEvent('cc:cart-max-reached', { detail: detail }));
  }

  document.addEventListener(
    'click',
    function (e) {
      const trigger = e.target.closest(
        '.bls__js-addtocart, [type="submit"][name="add"], button[name="add"]'
      );
      if (!trigger) return;

      const container =
        trigger.closest('form, .bls__product-item, [id^="product-form"]') || document;
      const qtyInput = container.querySelector(
        'quantity-input input[type="number"], input[name="quantity"]'
      );
      if (!qtyInput) return;

      const limits = getLimits(qtyInput);
      // Geen max gedefinieerd → laat door (geen check)
      if (limits.max === null) return;
      // Zonder product-id kunnen we niet sommeren → laat door
      if (!limits.productId) return;
      // Cart nog niet geladen → laat door (server doet eigen voorraad-check)
      if (!cartCacheReady) return;

      const inCart = getProductQtyInCart(limits.productId);
      const effMax = getEffectiveMax(qtyInput, limits); // = max - inCart
      let addQty = parseInt(qtyInput.value, 10);
      if (!isFinite(addQty) || addQty < 1) addQty = limits.min || 1;

      // In normale flow heeft clampInput effMax al op de selector toegepast,
      // dus addQty <= effMax. Dit pad triggert alleen bij stale cache,
      // user-bypass van DOM caps, of effMax==0 (selector op min vastgepind).
      if (addQty > effMax) {
        e.stopImmediatePropagation();
        e.preventDefault();
        const reason =
          limits.metaMax !== null && inCart + addQty > limits.metaMax
            ? 'meta-max'
            : 'inv-max';
        dispatchMaxReached({
          reason: reason,
          max: limits.max,
          metaMax: limits.metaMax,
          invMax: limits.invMax,
          inCart: inCart,
          productId: limits.productId,
        });
      }
    },
    true // capture: vóór theme handlers
  );

  // ----- Body inspection helpers (voor min-clamp in body) -----
  function clampQtyInBody(init, minV) {
    if (!init || !init.body) return;
    if (init.body instanceof FormData) {
      const q = parseInt(init.body.get('quantity'), 10);
      if (!isFinite(q) || q < minV) init.body.set('quantity', String(minV));
    } else if (typeof init.body === 'string') {
      if (init.body.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(init.body);
          const q = parseInt(parsed.quantity, 10);
          if (!isFinite(q) || q < minV) {
            parsed.quantity = minV;
            init.body = JSON.stringify(parsed);
          }
        } catch (e) {}
      } else {
        init.body = init.body.replace(/quantity=(\d+)/, function (_, q) {
          return 'quantity=' + (parseInt(q, 10) < minV ? minV : q);
        });
      }
    }
  }

  // ----- Click-context tracking voor min-clamp in body -----
  let lastMinV = null;
  let lastMinMarker = 0;
  document.addEventListener(
    'click',
    function (e) {
      const trigger = e.target.closest(
        '.bls__js-addtocart, [type="submit"][name="add"], button[name="add"]'
      );
      if (!trigger) return;
      const container =
        trigger.closest('form, .bls__product-item, [id^="product-form"]') || document;
      const qtyInput = container.querySelector(
        'quantity-input input[type="number"], input[name="quantity"]'
      );
      if (!qtyInput) {
        lastMinV = null;
        return;
      }
      const { min } = getLimits(qtyInput);
      lastMinV = min;
      lastMinMarker = Date.now();
    },
    true
  );

  // ----- Fetch-hook -----
  // 1) Min-clamping op /cart/add body (defense-in-depth).
  // 2) Cart-cache invalidation na succesvolle /cart/add of /cart/change
  //    zodat de click-phase max-check altijd actuele data heeft.
  const nativeFetch = window.fetch;
  if (nativeFetch && !nativeFetch._ccQtyHooked) {
    const hooked = function (input, init) {
      try {
        const url = typeof input === 'string' ? input : input && input.url;
        const isAdd = url && CART_ADD_RE.test(url);
        const isChange = url && CART_CHANGE_RE.test(url);
        if (isAdd && lastMinV && lastMinV > 1 && Date.now() - lastMinMarker < 1500) {
          clampQtyInBody(init, lastMinV);
          lastMinV = null;
        }
        const promise = nativeFetch.apply(this, arguments);
        if (isAdd || isChange) {
          promise.then(function (res) {
            if (res && res.ok) scheduleCartRefresh();
          }).catch(function () { /* ignore */ });
        }
        return promise;
      } catch (err) {
        return nativeFetch.apply(this, arguments);
      }
    };
    hooked._ccQtyHooked = true;
    window.fetch = hooked;
  }

  function refresh() {
    initAll();
    sanityCheckCart();
  }

  // ----- Init + re-init triggers -----
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      refresh();
      refreshCart();
    });
  } else {
    refresh();
    refreshCart();
  }
  document.addEventListener('shopify:section:load', refresh);

  // Re-init bij cart drawer DOM-replace (debounced om loops te voorkomen)
  if (window.MutationObserver) {
    const targets = ['form-mini-cart'];
    let debounceTimer = null;
    const schedule = function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(refresh, 50);
    };
    const start = function () {
      targets.forEach(function (id) {
        const el = document.getElementById(id);
        if (!el) return;
        new MutationObserver(schedule).observe(el, {
          childList: true,
          subtree: true,
        });
      });
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }
  }
})();
